import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Shield,
  Edit,
  Trash2,
  MoreHorizontal,
  UserCheck,
  UserX,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';

import { apiHelper } from '@services/api';
import { useUsers } from '@hooks/useUsers';
import DataTable from '@components/common/DataTable';
import Button, { IconButton } from '@components/common/Button';
import { Badge } from '@components/common/StatusBadge';
import Loading, { TableSkeleton } from '@components/common/Loading';
import Modal, { ModalBody, ModalFooter } from '@components/common/Modal';
import UserEntryModal from '@components/users/UserEntryModal';

const UserList = ({ searchQuery, statusFilter, roleFilter, refreshKey }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const queryClient = useQueryClient();

  // Build query parameters
  const queryParams = {
    search: searchQuery || '',
    status: statusFilter !== 'all' ? statusFilter : '',
    role: roleFilter !== 'all' ? roleFilter : '', // Don't normalize here, let the service handle it
    limit: 50
  };

  // Fetch users using the hook
  const {
    data: usersData,
    isLoading,
    error
  } = useUsers(queryParams);

  // Toggle user status mutation
  const toggleStatusMutation = useMutation(
    async ({ userId, isActive }) => {
      const response = await apiHelper.patch(`/users/${userId}/status`, {
        is_active: !isActive
      });
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries('users');
        toast.success(
          `User ${variables.isActive ? 'deactivated' : 'activated'} successfully`
        );
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user status');
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    async (userId) => {
      await apiHelper.delete(`/users/${userId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        setShowDeleteModal(false);
        setSelectedUser(null);
        toast.success('User deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete user');
      }
    }
  );

  const users = usersData || [];

  const getRoleBadgeVariant = (role) => {
    const variants = {
      ADMIN: 'error',
      admin: 'error',
      USER: 'primary',
      user: 'primary',
      CUSTOMER: 'info',
      customer: 'info',
      SUPPLIER: 'warning',
      supplier: 'warning'
    };
    return variants[role] || 'default';
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const tableColumns = [
    {
      key: 'user',
      label: 'User',
      render: (user) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {user.first_name} {user.last_name}
            </p>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <Mail className="w-3 h-3" />
              <span>{user.email}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Role',
      render: (user) => (
        <div className="flex flex-col gap-1">
          <Badge variant={getRoleBadgeVariant(user.role)} size="sm">
            <Shield className="w-3 h-3 mr-1" />
            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
          </Badge>
          {user.department && (
            <span className="text-xs text-gray-500">{user.department}</span>
          )}
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Contact',
      render: (user) => (
        <div className="text-sm">
          {user.phone && (
            <div className="flex items-center gap-1 text-gray-600 mb-1">
              <Phone className="w-3 h-3" />
              <span>{user.phone}</span>
            </div>
          )}
          {user.branch && (
            <div className="flex items-center gap-1 text-gray-500">
              <MapPin className="w-3 h-3" />
              <span>{user.branch.name}</span>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (user) => (
        <div className="flex flex-col gap-1">
          <Badge variant={user.is_active ? 'success' : 'default'} size="sm">
            {user.is_active ? (
              <>
                <UserCheck className="w-3 h-3 mr-1" />
                                Active
              </>
            ) : (
              <>
                <UserX className="w-3 h-3 mr-1" />
                                Inactive
              </>
            )}
          </Badge>
          {user.last_login && (
            <span className="text-xs text-gray-500">
                            Last login: {formatDate(user.last_login)}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'created',
      label: 'Created',
      render: (user) => (
        <div className="text-sm text-gray-600">
          {formatDate(user.created_at)}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (user) => (
        <div className="flex items-center gap-1">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleViewUser(user)}
            tooltip="View user"
          >
            <Eye className="w-4 h-4" />
          </IconButton>

          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleEditUser(user)}
            tooltip="Edit user"
          >
            <Edit className="w-4 h-4" />
          </IconButton>

          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => toggleStatusMutation.mutate({ userId: user.id, isActive: user.is_active })}
            className={user.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}
            tooltip={user.is_active ? 'Deactivate user' : 'Activate user'}
          >
            {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
          </IconButton>

          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteUser(user)}
            className="text-error-600 hover:bg-error-50"
            tooltip="Delete user"
          >
            <Trash2 className="w-4 h-4" />
          </IconButton>
        </div>
      )
    }
  ];

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
        <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-error-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Failed to load users
        </h3>
        <p className="text-gray-600 mb-4">
                    There was an error loading the user data.
        </p>
        <Button onClick={() => window.location.reload()}>
                    Try Again
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <TableSkeleton rows={8} columns={6} />;
  }

  return (
    <>
      <DataTable
        data={users}
        columns={tableColumns}
        loading={isLoading}
        emptyMessage="No users found"
      />

      {/* User Details Modal */}
      <Modal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        size="lg"
        title="User Details"
      >
        <ModalBody>
          {selectedUser && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedUser.first_name} {selectedUser.last_name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={getRoleBadgeVariant(selectedUser.role)}>
                      {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                    </Badge>
                    <Badge variant={selectedUser.is_active ? 'success' : 'default'}>
                      {selectedUser.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{selectedUser.email}</span>
                    </div>
                    {selectedUser.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{selectedUser.phone}</span>
                      </div>
                    )}
                    {selectedUser.branch && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {selectedUser.branch.name} - {selectedUser.branch.city}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Account Details</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    {selectedUser.department && (
                      <p><span className="font-medium">Department:</span> {selectedUser.department}</p>
                    )}
                    <p><span className="font-medium">Created:</span> {formatDate(selectedUser.created_at)}</p>
                    {selectedUser.last_login && (
                      <p><span className="font-medium">Last Login:</span> {formatDate(selectedUser.last_login)}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Permissions (if any) */}
              {selectedUser.permissions && selectedUser.permissions.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Permissions</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedUser.permissions.map((permission, index) => (
                      <div key={index} className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                        {permission.module?.name} - {permission.can_create && 'Create'} {permission.can_edit && 'Edit'} {permission.can_delete && 'Delete'}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowUserModal(false)}>
                        Close
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowUserModal(false);
              handleEditUser(selectedUser);
            }}
          >
                        Edit User
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="sm"
      >
        <ModalBody>
          <div className="text-center">
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-error-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Delete User
            </h3>
            <p className="text-gray-600 mb-4">
                            Are you sure you want to delete {selectedUser?.first_name} {selectedUser?.last_name}?
                            This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="ghost"
                onClick={() => setShowDeleteModal(false)}
              >
                                Cancel
              </Button>
              <Button
                variant="error"
                onClick={() => deleteUserMutation.mutate(selectedUser?.id)}
                loading={deleteUserMutation.isLoading}
              >
                                Delete
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Edit User Modal */}
      <UserEntryModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        user={selectedUser}
        mode="edit"
      />
    </>
  );
};

export default UserList;